import { Link } from "react-router-dom";
import {
  Wallet, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2,
  Calendar, DollarSign, ArrowRight, Loader2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatMXN } from "@/lib/utils";
import { useEstadoCuenta, useFacturasCliente } from "./api";

type Props = {
  clienteId: string;
  /** Se pasa para que los links a facturas vencidas tengan breadcrumb contextual. */
  fromLabel?: string;
};

/**
 * Vista de análisis financiero del cliente.
 *
 * Muestra un resumen "ejecutivo" (4 KPIs grandes) + detalles:
 *   - Estado general ("al corriente" / "con mora" / "sin actividad").
 *   - Próxima fecha de vencimiento con countdown.
 *   - Callout rojo con link a facturas vencidas si las hay.
 *
 * NO duplica el listado completo (eso está en el tab "Facturas"). Esta
 * vista sirve para que soporte conteste rápido: "¿cómo va el cliente?"
 */
export function EstadoCuentaSection({ clienteId, fromLabel }: Props) {
  const { data: estado, isLoading } = useEstadoCuenta(clienteId);
  // También traemos las facturas vencidas para poder linkearlas
  const { data: facturasData } = useFacturasCliente(clienteId);
  const facturasVencidas = (facturasData?.items ?? []).filter(
    (f) => f.estatus === "vencida",
  );

  if (isLoading || !estado) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 size={20} className="animate-spin" />
      </div>
    );
  }

  const totalFacturado = Number(estado.total_facturado);
  const totalPagado = Number(estado.total_pagado);
  const saldoPendiente = Number(estado.saldo_pendiente);
  // rc23+ — pagos huérfanos esperando una factura para aplicarse.
  const saldoAFavor = Number(estado.saldo_a_favor ?? 0);
  const tieneSaldoFavor = saldoAFavor > 0;
  const tieneVencidas = estado.facturas_vencidas > 0;
  const tieneDeuda = saldoPendiente > 0;
  const sinActividad = totalFacturado === 0;

  // Próxima fecha de vencimiento — countdown
  let proximoVencimiento: { fecha: string; diasRestantes: number } | null = null;
  if (estado.proxima_fecha_vencimiento) {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const venc = new Date(estado.proxima_fecha_vencimiento + "T00:00:00");
    const diffDays = Math.round(
      (venc.getTime() - hoy.getTime()) / (24 * 60 * 60 * 1000),
    );
    proximoVencimiento = {
      fecha: estado.proxima_fecha_vencimiento,
      diasRestantes: diffDays,
    };
  }

  // Estado general (semáforo)
  const estadoGeneral: {
    label: string;
    icon: typeof CheckCircle2;
    color: "success" | "destructive" | "warning" | "secondary";
    desc: string;
  } = tieneVencidas
    ? {
        label: "Con mora",
        icon: AlertTriangle,
        color: "destructive",
        desc: `${estado.facturas_vencidas} factura${estado.facturas_vencidas === 1 ? "" : "s"} vencida${estado.facturas_vencidas === 1 ? "" : "s"} pendiente${estado.facturas_vencidas === 1 ? "" : "s"} de pago.`,
      }
    : tieneDeuda
      ? {
          label: "Al corriente",
          icon: CheckCircle2,
          color: "warning",
          desc: `Con ${formatMXN(saldoPendiente)} por pagar, pero sin facturas vencidas.`,
        }
      : sinActividad
        ? {
            label: "Sin actividad",
            icon: Wallet,
            color: "secondary",
            desc: "Aún no se han emitido facturas a este cliente.",
          }
        : {
            label: "Al corriente",
            icon: CheckCircle2,
            color: "success",
            desc: "Sin deuda pendiente. Cliente al día.",
          };
  const EstadoIcon = estadoGeneral.icon;

  return (
    <div className="flex flex-col gap-4">
      {/* Estado general — banner destacado */}
      <div
        className={[
          "flex items-start gap-3 rounded-lg border p-4",
          estadoGeneral.color === "destructive"
            ? "border-destructive/40 bg-destructive/5"
            : estadoGeneral.color === "success"
              ? "border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20"
              : estadoGeneral.color === "warning"
                ? "border-amber-500/40 bg-amber-50 dark:bg-amber-950/20"
                : "bg-muted/30",
        ].join(" ")}
      >
        <EstadoIcon
          size={24}
          className={[
            "shrink-0 mt-0.5",
            estadoGeneral.color === "destructive" ? "text-destructive" : "",
            estadoGeneral.color === "success" ? "text-emerald-600 dark:text-emerald-400" : "",
            estadoGeneral.color === "warning" ? "text-amber-600 dark:text-amber-400" : "",
            estadoGeneral.color === "secondary" ? "text-muted-foreground" : "",
          ].filter(Boolean).join(" ")}
        />
        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold">{estadoGeneral.label}</h3>
            <Badge variant={estadoGeneral.color}>
              Saldo: {formatMXN(saldoPendiente)}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">{estadoGeneral.desc}</p>
        </div>
      </div>

      {/* rc23+ — Banner saldo a favor: pagos huérfanos esperando factura */}
      {tieneSaldoFavor && (
        <div className="flex items-start gap-3 rounded-lg border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/20 p-3">
          <Wallet size={20} className="shrink-0 mt-0.5 text-emerald-600 dark:text-emerald-400" />
          <div className="flex flex-col gap-0.5 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                Saldo a favor disponible
              </span>
              <Badge variant="success" className="font-mono">
                {formatMXN(saldoAFavor)}
              </Badge>
            </div>
            <p className="text-[11px] text-muted-foreground">
              Crédito a favor del cliente. Se aplicará automáticamente a la
              próxima factura que se le emita.
            </p>
          </div>
        </div>
      )}

      {/* 4 KPIs */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <KPICard
          icon={TrendingUp}
          label="Total facturado"
          value={formatMXN(totalFacturado)}
          iconColor="text-blue-600 dark:text-blue-400"
        />
        <KPICard
          icon={TrendingDown}
          label="Total pagado"
          value={formatMXN(totalPagado)}
          iconColor="text-emerald-600 dark:text-emerald-400"
        />
        <KPICard
          icon={DollarSign}
          label="Saldo pendiente"
          value={formatMXN(saldoPendiente)}
          variant={saldoPendiente > 0 ? "destructive" : "success"}
          iconColor={
            saldoPendiente > 0
              ? "text-destructive"
              : "text-emerald-600 dark:text-emerald-400"
          }
        />
        <KPICard
          icon={AlertTriangle}
          label="Facturas vencidas"
          value={String(estado.facturas_vencidas)}
          sublabel={
            estado.facturas_pendientes > 0
              ? `${estado.facturas_pendientes} pendiente${estado.facturas_pendientes === 1 ? "" : "s"} (no vencidas)`
              : "Ninguna pendiente"
          }
          variant={tieneVencidas ? "destructive" : undefined}
          iconColor={
            tieneVencidas
              ? "text-destructive"
              : "text-muted-foreground"
          }
        />
      </div>

      {/* Próximo vencimiento */}
      {proximoVencimiento && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-start gap-3">
            <Calendar size={18} className="mt-0.5 shrink-0 text-primary" />
            <div className="flex flex-col gap-1 flex-1">
              <h4 className="text-sm font-semibold">Próximo vencimiento</h4>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-muted-foreground">Fecha:</span>
                <span className="font-mono">
                  {formatDate(proximoVencimiento.fecha)}
                </span>
                {proximoVencimiento.diasRestantes < 0 ? (
                  <Badge variant="destructive">
                    {Math.abs(proximoVencimiento.diasRestantes)} día
                    {Math.abs(proximoVencimiento.diasRestantes) === 1 ? "" : "s"} de atraso
                  </Badge>
                ) : proximoVencimiento.diasRestantes === 0 ? (
                  <Badge variant="warning">Vence hoy</Badge>
                ) : proximoVencimiento.diasRestantes <= 5 ? (
                  <Badge variant="warning">
                    En {proximoVencimiento.diasRestantes} día
                    {proximoVencimiento.diasRestantes === 1 ? "" : "s"}
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    En {proximoVencimiento.diasRestantes} días
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Callout rojo con facturas vencidas */}
      {tieneVencidas && facturasVencidas.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="mt-0.5 shrink-0 text-destructive" />
            <div className="flex flex-col gap-2 flex-1">
              <div>
                <h4 className="text-sm font-semibold text-destructive">
                  Facturas vencidas — acción requerida
                </h4>
                <p className="text-xs text-destructive/80 mt-0.5">
                  {facturasVencidas.length} factura{facturasVencidas.length === 1 ? "" : "s"} con
                  saldo pendiente por un total de{" "}
                  <b>
                    {formatMXN(
                      facturasVencidas.reduce(
                        (sum, f) => sum + Number(f.saldo),
                        0,
                      ),
                    )}
                  </b>
                  . Contacta al cliente o registra el pago.
                </p>
              </div>
              <div className="flex flex-col gap-1 rounded-md border border-destructive/30 bg-card p-2">
                {facturasVencidas.slice(0, 5).map((f) => (
                  <Link
                    key={f.id}
                    to={`/facturas/${f.id}`}
                    state={
                      fromLabel
                        ? { from: `/clientes/${clienteId}`, fromLabel }
                        : undefined
                    }
                    className="flex items-center justify-between gap-2 rounded px-2 py-1 text-xs hover:bg-destructive/10"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono">{f.folio}</span>
                      <span className="text-muted-foreground">·</span>
                      <span className="text-muted-foreground">
                        vence {formatDate(f.fecha_vencimiento)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span className="font-semibold text-destructive">
                        {formatMXN(f.saldo)}
                      </span>
                      <ArrowRight size={10} className="text-muted-foreground" />
                    </div>
                  </Link>
                ))}
                {facturasVencidas.length > 5 && (
                  <p className="text-[10px] text-muted-foreground text-center px-2 py-1">
                    +{facturasVencidas.length - 5} facturas vencidas más. Ve a la
                    pestaña Facturas para el listado completo.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════
function KPICard({
  icon: Icon, label, value, sublabel, variant, iconColor,
}: {
  icon: typeof TrendingUp;
  label: string;
  value: string;
  sublabel?: string;
  variant?: "destructive" | "success";
  iconColor?: string;
}) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
      <div className="flex items-center gap-1.5">
        <Icon size={12} className={iconColor ?? "text-muted-foreground"} />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
      </div>
      <span
        className={[
          "text-lg font-semibold leading-none",
          variant === "destructive" ? "text-destructive" : "",
          variant === "success" ? "text-emerald-600 dark:text-emerald-400" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {value}
      </span>
      {sublabel && (
        <span className="text-[10px] text-muted-foreground">{sublabel}</span>
      )}
    </div>
  );
}
